/**
 * AgentDecision — the eligibility call a recruiter makes while WATCHING a live
 * conversation, surfaced inside the Guide-agent popover (owner ask 2026-08-06:
 * "under Guide agent I need the option to either say eligible — send link to
 * schedule call, or Not eligible and give the reason why so the agent can
 * kindly end the chat, or for no reason given").
 *
 * WHY THIS EXISTS SEPARATELY FROM DeterminationComposer. The composer already
 * owns these outcomes, but on the lead page it only mounts when the thread is
 * `awaiting_review` or carries an open prescreen flag (AgentPanel's
 * `determinationCtx`). Mid-conversation, with nothing flagged, a recruiter who
 * had already decided had no way to say so without a trip to the review queue.
 * This is that decision, three buttons wide.
 *
 * A DECISION IS NOT A STEER. A steer is a nudge — the conversation keeps its
 * course. Each of these posts a real determination: the agent composes ONE
 * friendly SMS, AUTO-SENDS it, and the not-eligible pair move the lead's
 * lifecycle to `not_eligible` (+ a DQ record, which the CTMS sweep syncs to
 * CRIO). So the click never fires the send: picking an option opens a form, and
 * a second, explicitly-labelled button is the commit.
 *
 * The words the lead hears are NOT authored here. Everything below feeds
 * POST /api/crm/agent/determinations verbatim — the same endpoint, the same
 * per-outcome framing in `texting_agent.determination`, the same validator
 * stack. The only thing this file owns is the internal write-up that stands in
 * when the recruiter types nothing, and even that is written so the outcome's
 * own framing does the talking.
 *
 * Selection is CONTROLLED by the parent so the popover can hold exactly one
 * open form at a time (staging a steer clears a decision and vice versa); the
 * typed fields stay local, and since the popover is `hidden`-toggled rather
 * than unmounted, a half-written reason survives closing and reopening it.
 */
import { useState } from 'react';
import { Ban, CheckCircle2, Loader2, Send, X } from 'lucide-react';
import api from '../../../services/api';
import { fieldCls } from '../filters';
import { Z } from '../ui';
import { DQ_CATEGORIES } from '../../lib';

export type DecisionKey = 'proceed' | 'not_eligible_reason' | 'not_eligible_now';

interface DecisionDef {
  key: DecisionKey;
  label: string;
  /** Hover/tap explainer — same discipline as the steers and the composer. */
  tip: string;
  tone: 'go' | 'stop';
  /** The lead-facing reason is required (posted as `pivot_reason`). */
  requiresReason?: boolean;
  /** Copy on the commit button. */
  commit: string;
  /** What the recruiter sees after it lands. */
  flash: string;
}

/**
 * The owner's three. `proceed` and `not_eligible_reason` are the composer's
 * outcomes verbatim; `not_eligible_now` is the "no reason given" close — the
 * backend has accepted it since the determination workflow shipped
 * (DETERMINATION_OUTCOMES), it simply had no button anywhere.
 */
export const DECISIONS: DecisionDef[] = [
  {
    key: 'proceed',
    label: 'Eligible · send link',
    tone: 'go',
    tip: 'They look eligible. The agent texts the good news, offers a quick call with our team to set up the screening visit, and sends the scheduling link. Add anything else you want relayed below.',
    commit: 'Send the good news + link',
    flash: 'Eligible — the agent is texting them the scheduling link',
  },
  {
    key: 'not_eligible_reason',
    label: 'Not eligible · give reason',
    tone: 'stop',
    requiresReason: true,
    tip: 'Closes the chat kindly WITH your reason: the agent relays the reason you write (softened, scoped to this study), adds that we get new studies all the time, and invites them to apply again. Moves the lead to Not eligible (syncs to CRIO).',
    commit: 'Send & close as not eligible',
    flash: 'Not eligible — the agent is relaying your reason kindly',
  },
  {
    key: 'not_eligible_now',
    label: 'Not eligible · no reason',
    tone: 'stop',
    tip: 'Closes the chat kindly WITHOUT a reason: the agent thanks them, says this study was not the right match, and invites them to apply again for future studies. It never states a medical reason or references their answers. Moves the lead to Not eligible (syncs to CRIO).',
    commit: 'Send & close as not eligible',
    flash: 'Not eligible — the agent is closing it out kindly',
  },
];

/**
 * The stand-in write-up per outcome. This is the `summary` the delivery prompt
 * reads as "THE RECRUITER WROTE (your ONLY source for what to say)", and it is
 * also what lands on the timeline as the record — so it says what was decided
 * and nothing the outcome's own framing does not already allow.
 *
 * `proceed` ALWAYS carries the call-and-link sentence, and a recruiter note is
 * appended rather than substituted: the button promises a scheduling link, and
 * a note that happened not to mention a call must not quietly drop it.
 */
const BASE_SUMMARY: Record<DecisionKey, string> = {
  proceed:
    'Eligible for this study. Tell them the good news, offer a quick call with our team to get the screening visit scheduled, and include the scheduling link.',
  not_eligible_reason:
    'Not eligible for this study. Relay the reason below to the lead, kindly and scoped to this study.',
  not_eligible_now:
    'Not eligible for this study. Do not give a reason — thank them for their time, say this study was not the right match, and invite them to apply again as new studies open.',
};

/** Machine reason → what a recruiter needs to read. */
const REFUSAL: Record<string, string> = {
  in_flight: 'A determination on this thread is already being delivered — give it a moment, then reload.',
  conversation_closed: 'This conversation is closed, so no determination can be delivered on it.',
  scheduler_conversation: 'The scheduling assistant has this thread and only sends its own fixed messages. Text or call the lead yourself; "Resolve" still clears the flag.',
  not_found: 'This conversation could not be found — reload the page.',
  user_required: 'Your session expired — sign in again.',
  summary_required: 'Write what the agent should relay before sending.',
  pivot_reason_required: 'Write the reason the agent should give the lead.',
  invalid_dq_category: 'That reason category is not one we track — pick another.',
};

export default function AgentDecision({
  conversationId, disabled = false, selected, onSelect, onError, onDone,
}: {
  conversationId: string;
  /** Closed thread / no session — the buttons grey out, same as the steers. */
  disabled?: boolean;
  selected: DecisionKey | null;
  onSelect: (key: DecisionKey | null) => void;
  onError: (msg: string) => void;
  /** Fired after a determination is accepted, with the recruiter-facing flash. */
  onDone: (flash: string) => void;
}) {
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [dqCategory, setDqCategory] = useState('');
  const [busy, setBusy] = useState(false);

  const def = DECISIONS.find((d) => d.key === selected) || null;
  const notEligible = selected === 'not_eligible_reason' || selected === 'not_eligible_now';
  const canSubmit = Boolean(def) && !busy && (!def?.requiresReason || reason.trim().length > 0);

  const clear = () => {
    onSelect(null);
    setNote('');
    setReason('');
    setDqCategory('');
  };

  const submit = async () => {
    if (!def || !canSubmit) return;
    setBusy(true);
    try {
      const cleanNote = note.trim();
      const cleanReason = reason.trim();
      const summary = def.key === 'proceed'
        ? (cleanNote ? `${BASE_SUMMARY.proceed}\n\nAlso relay: ${cleanNote}` : BASE_SUMMARY.proceed)
        : def.key === 'not_eligible_reason'
          ? `${BASE_SUMMARY.not_eligible_reason}\n\nReason: ${cleanReason}`
          : (cleanNote ? `${BASE_SUMMARY.not_eligible_now}\n\nInternal note: ${cleanNote}` : BASE_SUMMARY.not_eligible_now);

      await api.post('/api/crm/agent/determinations', {
        conversation_id: conversationId,
        // Not an escalation answer — this is a recruiter deciding mid-thread.
        // The submit resolves every open flag on the conversation anyway.
        kind: 'eligibility',
        outcome: def.key,
        summary,
        ...(def.key === 'not_eligible_reason' ? { pivot_reason: cleanReason } : {}),
        ...(notEligible && dqCategory ? { dq_category: dqCategory } : {}),
      });
      clear();
      onDone(def.flash);
    } catch (err: any) {
      const code = err?.response?.data?.error;
      onError(REFUSAL[code] || err?.response?.data?.detail || (code ? String(code) : null) || err?.message || 'That determination failed — try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 border-t border-gray-200 pt-2.5 dark:border-gray-700">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Decide eligibility
      </div>
      <p className="mt-0.5 text-[11px] leading-snug text-gray-400 dark:text-gray-500">
        Unlike the steers above, this is a decision: the agent texts it and screening ends.
      </p>

      {/* One column below `sm` for the same reason the steer list is: each row
          carries its own explainer, which a 2-up grid has no width for. */}
      <div className="mt-1.5 grid grid-cols-2 gap-1.5 max-sm:grid-cols-1">
        {DECISIONS.map((d) => {
          const lit = selected === d.key;
          const litCls = d.tone === 'go'
            ? 'border-green-500 bg-green-50 text-green-700 dark:border-green-600 dark:bg-green-900/20 dark:text-green-300'
            : 'border-red-400 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300';
          return (
            <div key={d.key} className={`group relative ${d.key === 'proceed' ? 'max-sm:col-span-1 sm:col-span-2' : ''}`}>
              <button
                type="button"
                disabled={disabled || busy}
                onClick={() => onSelect(lit ? null : d.key)}
                aria-pressed={lit}
                className={`flex w-full items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 text-center text-xs font-medium transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 max-sm:justify-start max-sm:text-left ${lit
                  ? litCls
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'}`}
              >
                {d.tone === 'go'
                  ? <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                  : <Ban className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />}
                <span className="min-w-0">
                  {d.label}
                  {/* A phone has no hover, so the explainer rides inside the
                      button there and the row stays one tap target. (No `block`
                      alongside `line-clamp-2` — Tailwind emits `.block` after
                      `.line-clamp-2` and would undo the clamp.) */}
                  <span className="mt-0.5 line-clamp-2 text-[11px] font-normal leading-snug text-gray-500 dark:text-gray-400 sm:hidden">
                    {d.tip}
                  </span>
                </span>
              </button>
              <div
                role="tooltip"
                className={`pointer-events-none absolute bottom-full left-1/2 ${Z.menu} mb-1.5 hidden w-60 -translate-x-1/2 rounded-lg border border-gray-200 bg-white p-2 text-left text-[11px] leading-relaxed text-gray-600 shadow-lg sm:group-hover:block sm:group-focus-within:block dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300`}
              >
                <span className="font-semibold text-gray-900 dark:text-gray-100">{d.label}.</span> {d.tip}
              </div>
            </div>
          );
        })}
      </div>

      {def && (
        <div
          className={`mt-2 rounded-lg border p-2 ${def.tone === 'go'
            ? 'border-green-200 bg-green-50/60 dark:border-green-900/50 dark:bg-green-900/15'
            : 'border-red-200 bg-red-50/60 dark:border-red-900/50 dark:bg-red-900/15'}`}
        >
          <div className="flex items-start justify-between gap-2">
            <p className="text-[11px] leading-relaxed text-gray-600 dark:text-gray-300">{def.tip}</p>
            <button
              type="button"
              onClick={clear}
              aria-label="Cancel this decision"
              className="rounded p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {def.requiresReason ? (
            <>
              <textarea
                value={reason}
                onChange={(ev) => setReason(ev.target.value)}
                rows={2}
                maxLength={1000}
                autoFocus
                aria-label="Reason shared with the lead"
                placeholder="The reason the agent will give the lead — e.g. “the injectable medication would be exclusionary for this study”. It is delivered softly, scoped to this study, with the future-studies invitation."
                className={`${fieldCls} mt-2 w-full resize-y`}
              />
              <p className="mt-1 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
                The agent stays inside your words: no added medical detail, no numbers you didn’t write, never “failed” or “disqualified”.
              </p>
            </>
          ) : (
            <textarea
              value={note}
              onChange={(ev) => setNote(ev.target.value)}
              rows={2}
              maxLength={1500}
              aria-label={def.key === 'proceed' ? 'Anything else to relay (optional)' : 'Internal note (optional)'}
              placeholder={def.key === 'proceed'
                ? 'Optional — anything else to relay, e.g. “mention we have evening appointments”. The good news and the scheduling link go either way.'
                : 'Optional — a note for the record only. The lead is given no reason.'}
              className={`${fieldCls} mt-2 w-full resize-y`}
            />
          )}

          {/* Feeds the DQ record, not the message. Blank = the backend infers
              it from the write-up (falling back to "other"). */}
          {notEligible && (
            <select
              value={dqCategory}
              onChange={(ev) => setDqCategory(ev.target.value)}
              aria-label="Disqualification category"
              className={`${fieldCls} mt-2 w-full`}
            >
              <option value="">Reason category: pick automatically</option>
              {DQ_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              disabled={!canSubmit}
              onClick={submit}
              className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-3 text-xs font-medium text-white transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 ${def.tone === 'go'
                ? 'bg-primary-600 hover:bg-primary-700'
                : 'bg-red-600 hover:bg-red-700'}`}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              {def.commit}
            </button>
            <span className="text-[11px] text-gray-400 dark:text-gray-500">
              {def.tone === 'go'
                ? 'Sends as soon as you click — no second approval step.'
                : 'Sends as soon as you click, and moves the lead to Not eligible.'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
